// SalaryTemplates.js - Updated version
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AddSalaryTemplate from './AddSalaryTemplate';
import EditSalaryTemplate from './EditSalaryTemplate';
import TemplateDetails from './TemplateDetails';

const SalaryTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [componentCounts, setComponentCounts] = useState({}); // Store component counts
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [sortBy, setSortBy] = useState('name');
  const [userPermissions, setUserPermissions] = useState({
    can_view: false,
    can_add: false,
    can_edit: false,
    can_delete: false,
    loaded: false
  });

  useAuth();
  const navigate = useNavigate();

  const API_BASE = process.env.REACT_APP_API_BASE;

  const fetchUserPermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setUserPermissions({
          can_view: false,
          can_add: false,
          can_edit: false,
          can_delete: false,
          loaded: true
        });
        return;
      }

      const response = await axios.get(`${API_BASE}/get_user_permissions.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.permissions) {
        const salaryTemplatesPermissions = response.data.permissions.find(
          p => p.module_name === 'SalaryTemplates' || p.module_name === 'PayTemplates'
        );

        if (salaryTemplatesPermissions) {
          setUserPermissions({
            can_view: Boolean(salaryTemplatesPermissions.can_view),
            can_add: Boolean(salaryTemplatesPermissions.can_add),
            can_edit: Boolean(salaryTemplatesPermissions.can_edit),
            can_delete: Boolean(salaryTemplatesPermissions.can_delete),
            loaded: true
          });
        } else {
          setUserPermissions({
            can_view: false,
            can_add: false,
            can_edit: false,
            can_delete: false,
            loaded: true
          });
        }
      } else {
        setUserPermissions({
          can_view: false,
          can_add: false,
          can_edit: false,
          can_delete: false,
          loaded: true
        });
      }
    } catch (error) {
      setUserPermissions({
        can_view: false,
        can_add: false,
        can_edit: false,
        can_delete: false,
        loaded: true
      });
    }
  }, [API_BASE]);

  const getComponentCount = useCallback(async (templateId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/get_template_component_count.php`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { template_id: templateId }
      });

      if (response.data.success) {
        return response.data.component_count || 0;
      }
      return 0;
    } catch (error) {
      console.error('Error fetching component count:', error);
      return 0;
    }
  }, [API_BASE]);

  const fetchComponentCounts = useCallback(async (templatesList) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/get_template_component_count.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.counts) {
        setComponentCounts(response.data.counts);
      } else {
        // Fallback: fetch counts individually
        const counts = {};
        for (const template of templatesList) {
          const count = await getComponentCount(template.template_id);
          counts[template.template_id] = count;
        }
        setComponentCounts(counts);
      }
    } catch (error) {
      console.error('Error fetching component counts:', error);
      // Initialize with zeros
      const counts = {};
      templatesList.forEach(template => {
        counts[template.template_id] = 0;
      });
      setComponentCounts(counts);
    }
  }, [API_BASE, getComponentCount]);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/get_salary_templates.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const templatesData = response.data.templates;
        setTemplates(templatesData);

        // Fetch component counts for all templates
        fetchComponentCounts(templatesData);
      } else {
        setError(response.data.error || 'Failed to fetch salary templates');
      }
    } catch (error) {
      setError('Failed to fetch salary templates');
      console.error('Error fetching salary templates:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, fetchComponentCounts]);

  useEffect(() => {
    const initializeData = async () => {
      await fetchUserPermissions();
      await fetchTemplates();
    };

    initializeData();
  }, [fetchUserPermissions, fetchTemplates]);

  const handleDelete = async (templateId) => {
    if (!userPermissions.can_delete) {
      alert('You do not have permission to delete salary templates');
      return;
    }

    if (window.confirm('Are you sure you want to delete this salary template? This action cannot be undone.')) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.post(`${API_BASE}/delete_salary_template.php`,
          { template_id: templateId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          fetchTemplates();
          alert('Salary template deleted successfully');
        } else {
          setError(response.data.error || 'Failed to delete salary template');
        }
      } catch (error) {
        setError(error.response?.data?.error || 'Failed to delete salary template');
        console.error('Error deleting salary template:', error);
      }
    }
  };

  const handleEdit = (template) => {
    if (!userPermissions.can_edit) {
      alert('You do not have permission to edit salary templates');
      return;
    }
    setSelectedTemplate(template);
    setShowEditModal(true);
  };

  const handleViewDetails = (template) => {
    if (!userPermissions.can_view) {
      alert('You do not have permission to view salary template details');
      return;
    }
    setSelectedTemplate(template);
    setShowDetailsModal(true);
  };

  const handleManageComponents = (template) => {
    if (!userPermissions.can_edit) {
      alert('You do not have permission to manage template components');
      return;
    }
    // Navigate to component management page
    navigate(`/salary-template-components/${template.template_id}`, {
      state: { template }
    });
  };

  const handleCloneTemplate = async (template) => {
    if (!userPermissions.can_add) {
      alert('You do not have permission to clone templates');
      return;
    }

    if (window.confirm(`Clone template "${template.template_name}"?`)) {
      try {
        setLoading(true);
        const token = localStorage.getItem('authToken');
        const response = await axios.post(`${API_BASE}/clone_salary_template.php`,
          { template_id: template.template_id },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          fetchTemplates();
          alert('Template cloned successfully');
        } else {
          setError(response.data.error || 'Failed to clone template');
        }
      } catch (error) {
        setError(error.response?.data?.error || 'Failed to clone template');
        console.error('Error cloning template:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  // Navigation handlers
  const goToDashboard = () => navigate('/dashboard');
  const goToHome = () => navigate('/');
  const goToAdminPanel = () => navigate('/admin-panel');
  const goToEmployees = () => navigate('/employees');
  const goToPayComponents = () => navigate('/pay-components');

  // Filter templates
  const filteredTemplates = templates.filter(template => {
    const matchesSearch =
      template.template_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  // Sort templates
  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.template_name.localeCompare(b.template_name);
      case 'date':
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      case 'components':
        return (componentCounts[b.template_id] || 0) - (componentCounts[a.template_id] || 0);
      default:
        return 0;
    }
  });

  // Get component count from state
  const getComponentCountDisplay = (templateId) => {
    return componentCounts[templateId] || 0;
  };

  // Permission check functions
  const canViewTemplates = () => userPermissions.can_view;
  const canAddTemplate = () => userPermissions.can_add;
  const canEditTemplate = () => userPermissions.can_edit;
  const canDeleteTemplate = () => userPermissions.can_delete;

  if (loading || !userPermissions.loaded) {
    return (
      <div className="d-flex justify-content-center align-items-center youtube-page-container" style={{ height: '50vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading salary templates...</p>
        </div>
      </div>
    );
  }

  if (!canViewTemplates()) {
    return (
      <div className="container-fluid py-4 youtube-page-container">
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center py-5">
                <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                <h3 className="mt-3 text-warning">Access Denied</h3>
                <p className="text-muted">You do not have permission to view salary templates.</p>
                <button
                  className="btn btn-primary mt-3"
                  onClick={goToDashboard}
                >
                  <i className="bi bi-speedometer2 me-2"></i>
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4 youtube-page-container">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body py-4">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="btn-group me-3" role="group">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={goToHome}
                        title="Go to Home"
                      >
                        <i className="bi bi-house"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={goToDashboard}
                        title="Go to Dashboard"
                      >
                        <i className="bi bi-speedometer2"></i>
                      </button>
                    </div>
                    <div>
                      <h2 className="h4 mb-1 fw-bold text-dark">
                        <i className="bi bi-file-earmark-text me-2 text-primary"></i>
                        Salary Templates Management
                        {!canAddTemplate() && !canEditTemplate() && !canDeleteTemplate() && (
                          <span className="badge bg-warning ms-2">View Only</span>
                        )}
                      </h2>
                      <p className="text-muted mb-0">
                        Create and manage salary templates with earnings and deductions
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  <div className="d-flex justify-content-end align-items-center">
                    {canAddTemplate() && (
                      <button
                        className="btn btn-primary btn-lg"
                        onClick={() => setShowAddModal(true)}
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        Create New Template
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card bg-light border-0">
            <div className="card-body py-3">
              <div className="row text-center">
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                    onClick={goToHome}
                  >
                    <i className="bi bi-house me-2"></i>
                    Home
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center"
                    onClick={goToDashboard}
                  >
                    <i className="bi bi-speedometer2 me-2"></i>
                    Dashboard
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-info w-100 d-flex align-items-center justify-content-center"
                    onClick={goToEmployees}
                  >
                    <i className="bi bi-people me-2"></i>
                    Employees
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-warning w-100 d-flex align-items-center justify-content-center"
                    onClick={goToPayComponents}
                  >
                    <i className="bi bi-calculator me-2"></i>
                    Pay Components
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                    disabled
                  >
                    <i className="bi bi-file-earmark-text me-2"></i>
                    Salary Templates
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-dark w-100 d-flex align-items-center justify-content-center"
                    onClick={goToAdminPanel}
                  >
                    <i className="bi bi-gear me-2"></i>
                    Admin Panel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="row g-3 align-items-center">
                <div className="col-md-4">
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <i className="bi bi-search text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Search templates..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        className="btn btn-outline-secondary border-start-0"
                        onClick={() => setSearchTerm('')}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="All">All Templates</option>
                    <option value="Active">Active Only</option>
                    <option value="Inactive">Inactive Only</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="name">Sort by Name</option>
                    <option value="date">Sort by Date Created</option>
                    <option value="components">Sort by Components</option>
                  </select>
                </div>
                <div className="col-md-2 text-end">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={fetchTemplates}
                    title="Refresh"
                  >
                    <i className="bi bi-arrow-clockwise"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <div>{error}</div>
              <button
                type="button"
                className="btn-close ms-auto"
                onClick={() => setError('')}
              ></button>
            </div>
          </div>
        </div>
      )}

      {/* Templates List */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body p-0">
              {sortedTemplates.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-file-earmark-text display-1 text-muted"></i>
                  <h4 className="mt-3 text-muted">No salary templates found</h4>
                  <p className="text-muted">
                    {templates.length === 0
                      ? "Get started by creating your first salary template."
                      : "No templates match your search criteria."
                    }
                  </p>
                  {templates.length === 0 && canAddTemplate() && (
                    <button
                      className="btn btn-primary mt-2"
                      onClick={() => setShowAddModal(true)}
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      Create First Template
                    </button>
                  )}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4">Template Name</th>
                        <th>Description</th>
                        <th>Components</th>
                        <th>Created</th>
                        <th className="text-end pe-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTemplates.map((template) => (
                        <tr key={template.template_id} className="template-row">
                          <td className="ps-4">
                            <div className="d-flex align-items-center">
                              <div className="template-icon bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                                style={{ width: '40px', height: '40px' }}
                              >
                                <i className="bi bi-file-earmark-text"></i>
                              </div>
                              <div>
                                <div className="fw-semibold">
                                  {template.template_name}
                                </div>
                                <small className="text-muted">ID: {template.template_id}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '250px' }} title={template.description}>
                              {template.description || 'No description'}
                            </div>
                          </td>
                          <td>
                            <span className="badge bg-info">
                              <i className="bi bi-grid-3x3-gap me-1"></i>
                              {getComponentCountDisplay(template.template_id)} components
                            </span>
                          </td>
                          <td>
                            <small className="text-muted">
                              {template.created_at ? new Date(template.created_at).toLocaleDateString() : 'N/A'}
                            </small>
                          </td>
                          <td className="text-end pe-4">
                            <div className="btn-group" role="group">
                              <button
                                className="btn btn-outline-info btn-sm"
                                onClick={() => handleViewDetails(template)}
                                title="View Details"
                              >
                                <i className="bi bi-eye"></i>
                              </button>
                              {canEditTemplate() && (
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => handleEdit(template)}
                                  title="Edit Template"
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                              )}
                              {canAddTemplate() && (
                                <button
                                  className="btn btn-outline-success btn-sm"
                                  onClick={() => handleCloneTemplate(template)}
                                  title="Clone Template"
                                >
                                  <i className="bi bi-copy"></i>
                                </button>
                              )}
                              {canEditTemplate() && (
                                <button
                                  className="btn btn-outline-warning btn-sm"
                                  onClick={() => handleManageComponents(template)}
                                  title="Manage Components"
                                >
                                  <i className="bi bi-gear"></i>
                                </button>
                              )}
                              {canDeleteTemplate() && (
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleDelete(template.template_id)}
                                  title="Delete"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Footer */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card bg-light border-0">
            <div className="card-body py-3">
              <div className="row text-center">
                <div className="col-md-3">
                  <div className="fw-bold text-primary fs-4">{templates.length}</div>
                  <small className="text-muted">Total Templates</small>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-success fs-4">
                    {templates.filter(t => t.is_active === 1 || t.is_active === true).length}
                  </div>
                  <small className="text-muted">Active Templates</small>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-info fs-4">
                    {Object.values(componentCounts).reduce((sum, count) => sum + count, 0)}
                  </div>
                  <small className="text-muted">Total Components</small>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-warning fs-4">
                    {/* You would need to add logic to count templates with formulas */}
                    {templates.length}
                  </div>
                  <small className="text-muted">Templates with Formulas</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation Footer */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card border-0">
            <div className="card-body text-center py-3">
              <div className="btn-group" role="group">
                <button
                  className="btn btn-outline-primary"
                  onClick={goToHome}
                >
                  <i className="bi bi-house me-2"></i>
                  Back to Home
                </button>
                <button
                  className="btn btn-outline-success"
                  onClick={goToDashboard}
                >
                  <i className="bi bi-speedometer2 me-2"></i>
                  Back to Dashboard
                </button>
                <button
                  className="btn btn-outline-info"
                  onClick={goToEmployees}
                >
                  <i className="bi bi-people me-2"></i>
                  Go to Employees
                </button>
                <button
                  className="btn btn-outline-warning"
                  onClick={goToPayComponents}
                >
                  <i className="bi bi-calculator me-2"></i>
                  Go to Pay Components
                </button>
                <button
                  className="btn btn-outline-dark"
                  onClick={goToAdminPanel}
                >
                  <i className="bi bi-gear me-2"></i>
                  Go to Admin Panel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAddModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1040,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <AddSalaryTemplate
            show={showAddModal}
            onHide={() => setShowAddModal(false)}
            onTemplateAdded={fetchTemplates}
          />
        </div>
      )}

      {showEditModal && selectedTemplate && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1040,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <EditSalaryTemplate
            show={showEditModal}
            onHide={() => setShowEditModal(false)}
            template={selectedTemplate}
            onTemplateUpdated={fetchTemplates}
          />
        </div>
      )}

      {showDetailsModal && selectedTemplate && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1040,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <TemplateDetails
            show={showDetailsModal}
            onHide={() => setShowDetailsModal(false)}
            template={selectedTemplate}
          />
        </div>
      )}

      {/* CSS Styles */}
      <style jsx>{`
        .template-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .template-row:hover {
          background-color: rgba(0, 123, 255, 0.05);
        }
        
        .stats-card {
          border: none;
          border-radius: 10px;
          transition: all 0.3s;
        }
        
        .stats-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </div>
  );
};

export default SalaryTemplates;